import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { WalletLink } from "../components/WalletLink";
import { Skeleton } from "../components/Skeleton";

export function FreshSpinners() {
  // Filter state
  const [freshHours, setFreshHours] = useState(72);
  const [minWinEvents, setMinWinEvents] = useState(3);
  const [limit, setLimit] = useState(25);

  // Fetch fresh spinners with filters
  const freshSpinnersQuery = useQuery({
    queryKey: ["fresh-spinners-page", freshHours, minWinEvents, limit],
    queryFn: async () => {
      const { data, error } = await api.freshSpinners.get({
        query: { freshHours, minWinEvents, limit },
      });
      if (error) throw error;
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const presetFilters = [
    { label: "Last 24h", hours: 24, wins: 2 },
    { label: "Last 48h", hours: 48, wins: 3 },
    { label: "Last 72h", hours: 72, wins: 3 },
    { label: "This week", hours: 168, wins: 5 },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="page-title flex items-center gap-3">
          <span className="live-dot" />
          Fresh spinners
        </h1>
        <p className="section-hint mt-1">
          Wallets first seen recently that have been winning prizes in quick
          succession.
        </p>
      </div>

      {/* Filters */}
      <section className="card">
        <h2 className="section-title mb-4">Filters</h2>

        {/* Preset buttons */}
        <div className="seg mb-4">
          {presetFilters.map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setFreshHours(preset.hours);
                setMinWinEvents(preset.wins);
              }}
              className={`seg-btn ${
                freshHours === preset.hours && minWinEvents === preset.wins
                  ? "seg-btn-on"
                  : ""
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom filters */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              First seen within (hours)
            </label>
            <input
              type="number"
              value={freshHours}
              onChange={(e) => setFreshHours(Number(e.target.value))}
              min={1}
              max={720}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Minimum wins
            </label>
            <input
              type="number"
              value={minWinEvents}
              onChange={(e) => setMinWinEvents(Number(e.target.value))}
              min={1}
              max={100}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Results limit
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="select w-full py-2"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-title">
            Results
            {freshSpinnersQuery.data?.data && (
              <span className="text-gray-400 font-normal ml-2 num">
                ({freshSpinnersQuery.data.data.length} wallets)
              </span>
            )}
          </h2>
          {freshSpinnersQuery.isFetching && (
            <span className="text-sm text-gray-400">Refreshing…</span>
          )}
        </div>

        {freshSpinnersQuery.isLoading && (
          <Skeleton kind="table" rows={8} />
        )}

        {freshSpinnersQuery.error && (
          <p className="text-red-400 py-4">
            Could not load fresh spinners: {String(freshSpinnersQuery.error)}
          </p>
        )}

        {freshSpinnersQuery.data?.data &&
          freshSpinnersQuery.data.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-3 pr-4 font-medium">Wallet</th>
                    <th className="pb-3 pr-4 font-medium">First seen</th>
                    <th className="pb-3 pr-4 font-medium">Age</th>
                    <th className="pb-3 pr-4 font-medium">Wins</th>
                    <th className="pb-3 pr-4 font-medium">Win rate</th>
                    <th className="pb-3 pr-4 font-medium">Points</th>
                    <th className="pb-3 pr-4 font-medium">Pts/hour</th>
                    <th className="pb-3 pr-4 font-medium">Score</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {freshSpinnersQuery.data.data.map((spinner, index) => (
                    <tr key={spinner.walletAddress} className="table-row">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          {index < 3 && (
                            <span
                              className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-semibold num ${
                                index === 0
                                  ? "bg-yellow-400/15 text-yellow-400"
                                  : "bg-gray-700 text-gray-300"
                              }`}
                            >
                              {index + 1}
                            </span>
                          )}
                          <WalletLink address={spinner.walletAddress} />
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-400 text-xs num">
                        {new Date(spinner.firstSeenAt).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-gray-300 num">
                        {spinner.hoursOld < 1
                          ? `${Math.round(spinner.hoursOld * 60)}m`
                          : `${spinner.hoursOld.toFixed(1)}h`}
                      </td>
                      <td className="py-3 pr-4 text-gray-100 font-medium num">
                        {spinner.winEventCount}
                      </td>
                      <td className="py-3 pr-4 text-gray-300 num">
                        {spinner.winEventRate.toFixed(2)}/h
                      </td>
                      <td className="py-3 pr-4 text-gray-300 num">
                        {spinner.latestPoints?.toLocaleString() ?? "-"}
                      </td>
                      <td className="py-3 pr-4 num">
                        {spinner.pointsVelocity !== null ? (
                          <span
                            className={
                              spinner.pointsVelocity > 0
                                ? "text-green-400"
                                : spinner.pointsVelocity < 0
                                ? "text-red-400"
                                : "text-gray-400"
                            }
                          >
                            {spinner.pointsVelocity > 0 ? "+" : ""}
                            {spinner.pointsVelocity.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 num">
                        <span className="text-yellow-400 font-semibold">
                          {spinner.activityScore.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3">
                        {spinner.hasActiveSession ? (
                          <span className="pill bg-green-400/15 text-green-400">
                            <span className="live-dot" />
                            Active
                          </span>
                        ) : (
                          <span className="pill bg-gray-700 text-gray-400">
                            Idle
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {freshSpinnersQuery.data?.data?.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-gray-500">
              No fresh spinners match these filters.
            </p>
            <p className="text-gray-500 text-sm mt-1">
              Widen the time window or lower the minimum wins.
            </p>
          </div>
        )}
      </section>

      {/* Recent Wins for Top Spinners */}
      {freshSpinnersQuery.data?.data &&
        freshSpinnersQuery.data.data.length > 0 && (
          <section className="card">
            <h2 className="section-title mb-4">
              Recent wins from top spinners
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {freshSpinnersQuery.data.data.slice(0, 6).map((spinner) => (
                <div
                  key={spinner.walletAddress}
                  className="card-inset p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <WalletLink address={spinner.walletAddress} />
                    {spinner.hasActiveSession && <span className="live-dot" />}
                  </div>
                  {spinner.recentWins && spinner.recentWins.length > 0 ? (
                    <ul className="space-y-1.5">
                      {spinner.recentWins.slice(0, 3).map((win, idx) => (
                        <li
                          key={idx}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-gray-300 truncate flex-1 mr-2">
                            {win.nftName}
                          </span>
                          <span className="text-green-400 font-medium num">
                            ${win.insuredValue?.toFixed(0)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">No recent wins</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
    </div>
  );
}
